import { io, type Socket } from 'socket.io-client'
import { accessToken } from '@client/stores/authStore'
import { EditingPayload } from '@client/socket/editingTypes.ts'
import { createLockKeeper } from '@client/socket/lockKeeper.ts'

let socket: Socket | null = null
let lockKeeper: ReturnType<typeof createLockKeeper> | null = null
let socketListenersInstalled = false

export const getSocket = () => {
    if (!socket) {
        socket = io(import.meta.env.VITE_API_URL, {
            autoConnect: false,
            transports: ['websocket'],
            auth: { token: accessToken.value },
        })
        // asks for the held edit locks again whenever the socket (re)connects
        lockKeeper = createLockKeeper(socket)
    }

    if (!socketListenersInstalled) {
        socketListenersInstalled = true

        socket.io.on('reconnect_attempt', (attempt) => {
            socket!.auth = { token: accessToken.value }
            // console.log('[socket] reconnect attempt', attempt, '(auth refreshed)')
        })

        socket.on('connect', () => {
            // console.log('[socket] connected', socket?.id)
        })

        socket.on('disconnect', (reason) => {
            console.warn('[socket] disconnected:', reason)
        })

        socket.on('connect_error', (err) => {
            console.error('[socket] connect_error', err.message)

            if (String(err.message).toLowerCase().includes('unauthorized')) {
                console.warn('[socket] unauthorized → disconnecting to stop reconnect loop')
                try {
                    socket?.disconnect()
                } catch {}
            }
        })

        socket.io.on('error', (err: any) => {
            console.error('[socket] manager error', err?.message || err)
        })

        // console.log('[socket] listeners installed')
    }

    return socket
}

export const ensureSocketAuth = () => {
    const s = getSocket()
    s.auth = { token: accessToken.value }
}

export const connectSocketOnce = () => {
    const s = getSocket()
    ensureSocketAuth()
    if (!s.connected) s.connect()
    return s
}

export const emitEditLock = (payload: EditingPayload) => {
    const s = getSocket()
    ensureSocketAuth()
    if (!s.connected) s.connect()
    lockKeeper!.emit(payload)
}

let reconnectTimer: number | null = null

export const ensureSocketConnected = (reason = 'unknown') => {
    const s = getSocket()
    ensureSocketAuth()

    if (s.connected) {
        // console.log('[socket] already connected, skip reconnect (reason:', reason + ')')
        return
    }

    // if socket.io is already in the process of connecting/reconnecting, don't interfere
    if ((s as any).active) {
        // console.log('[socket] socket is active (connecting/reconnecting), skip manual reconnect (reason:', reason + ')')
        return
    }

    // avoid reconnect spam
    if (reconnectTimer) {
        // console.log('[socket] reconnect already scheduled, skip (reason:', reason + ')')
        return
    }

    console.warn('[socket] not connected → attempting reconnect (reason:', reason + ')')

    reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null
        try {
            s.connect()
        } catch (err) {
            console.error('[socket] connect() failed', err)
        }
    }, 200)
}

export const installSocketAutoReconnectHooks = () => {
    const onFocus = () => ensureSocketConnected('window focus')
    const onVisibility = () => {
        if (document.visibilityState === 'visible') {
            ensureSocketConnected('tab visible')
        }
    }
    const onOnline = () => ensureSocketConnected('network online')

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('online', onOnline)

    // console.log('[socket] auto-reconnect hooks installed')

    return () => {
        window.removeEventListener('focus', onFocus)
        document.removeEventListener('visibilitychange', onVisibility)
        window.removeEventListener('online', onOnline)
    }
}

// Debug helpers (intentionally verbose when used)
// ;(window as any).__socket = {
//     get: () => getSocket(),
//     disconnect: () => {
//         const s = getSocket()
//         console.warn('[socket] manual disconnect()')
//         s.disconnect()
//     },
//     connect: () => {
//         const s = getSocket()
//         console.warn('[socket] manual connect()')
//         ensureSocketAuth()
//         s.connect()
//     },
// }
